import React, { useState, useEffect } from 'react';
import { getSettings, getEmailTemplates, activateEmailTemplate, deleteEmailTemplate, getSmsTemplates, createSmsTemplate, deleteSmsTemplate, updateEmailTemplateGovernance, getSystemRules } from '../../data/api';
import { Settings, MessageSquare, Eye, Trash2, Mail } from 'lucide-react';
import { theme } from '../../data/colors';
import AdminPageHeader from './AdminPageHeader';

// Map theme to local COLORS for minimal change footprint
const COLORS = {
  gold: theme.accentPrimary,
  goldLight: theme.accentTintLight,
  goldMedium: theme.accentTintMedium,
  surface: theme.bgCard,
  surfaceWarm: theme.bgTabRail,
  cardBg: theme.bgCard,
  inputBg: theme.bgInput,
  textPrimary: theme.textPrimary,
  textSecondary: theme.textSecondary,
  textMuted: theme.textMuted,
  border: theme.borderLight,
  borderMedium: theme.borderMedium,
  success: theme.statusSuccess,
  successLight: theme.statusSuccessTint,
  error: theme.statusError,
  warning: theme.statusWarning,
  warningLight: theme.statusWarningTint,
  info: '#3b82f6',
  infoLight: 'rgba(59,130,246,0.15)',
};

const MessageTemplatesTab = () => {
  const [settings, setSettings] = useState({ bcc_email:'', active_auto_reply_template_id:'' });
  const [emailTmpls, setEmailTmpls] = useState([]);
  const [smsTmpls, setSmsTmpls] = useState([]);
  const [newSms, setNewSms] = useState({ name:'', template_type:'custom', message_text:'', media_urls:[] });
  const [showCreateSms, setShowCreateSms] = useState(false);
  const [preview, setPreview] = useState(null);
  // Pass 5 / M14 — governance UI state. sysRules is fetched once on mount;
  // null until fetch resolves, which keeps the governance row hidden so
  // pre-Pass-5 environments without the /api/system-rules endpoint render
  // the existing UI unchanged. savingId is used to dim the row controls
  // while a PATCH is in flight, preventing double-submits.
  const [sysRules, setSysRules] = useState(null);
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    getSettings().then(setSettings).catch(()=>{});
    getEmailTemplates().then(setEmailTmpls).catch(()=>{});
    getSmsTemplates().then(setSmsTmpls).catch(()=>{});
    // Pass 5 / M16 — fetch shared rules once for governance dropdowns.
    // Silent failure → governance row stays hidden.
    getSystemRules().then(setSysRules).catch(()=>{});
  }, []);

  const handleActivate = async (id) => { await activateEmailTemplate(id); getEmailTemplates().then(setEmailTmpls); };
  const handleDeleteTmpl = async (id) => { await deleteEmailTemplate(id); getEmailTemplates().then(setEmailTmpls); };
  // Pass 5 / M14 — governance update handler. Optimistic local update
  // for instant feedback, then refetch for source-of-truth. Failures
  // surface as alerts and refetch reverts the optimistic state.
  const handleUpdateGovernance = async (id, patch) => {
    setSavingId(id);
    setEmailTmpls(prev => prev.map(t => t.id === id ? { ...t, ...patch } : t));
    try {
      await updateEmailTemplateGovernance(id, patch);
      const fresh = await getEmailTemplates();
      setEmailTmpls(fresh);
    } catch (err) {
      alert(`Could not update governance: ${err?.response?.data?.detail || err.message}`);
      const fresh = await getEmailTemplates().catch(() => null);
      if (fresh) setEmailTmpls(fresh);
    } finally {
      setSavingId(null);
    }
  };
  const handleCreateSms = async () => {
    if (!newSms.name || !newSms.message_text) return;
    await createSmsTemplate(newSms);
    getSmsTemplates().then(setSmsTmpls);
    setNewSms({ name:'', template_type:'custom', message_text:'', media_urls:[] });
    setShowCreateSms(false);
  };
  const handleDeleteSms = async (id) => { await deleteSmsTemplate(id); getSmsTemplates().then(setSmsTmpls); };

  return (
    <div data-testid="templates-management">
      {/* Section Header — shared AdminPageHeader (Dashboard standard).
          Title/tagline wording preserved from the prior section header. */}
      <AdminPageHeader
        eyebrow="Templates"
        title="Template Management"
        tagline={`${emailTmpls.length} email · ${smsTmpls.length} SMS templates`}
      />

      {/* Global BCC Settings */}
      <div style={{
        background: COLORS.cardBg,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 12,
        padding: 20,
        marginBottom: 20,
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <Settings size={16} color={COLORS.gold} />
          <span style={{ fontSize: 14, fontWeight: 600, color: COLORS.textPrimary }}>Global Settings</span>
        </div>
        <div style={{
          background: COLORS.goldLight,
          borderRadius: 8,
          padding: 14,
          fontSize: 13,
          color: COLORS.textSecondary,
          lineHeight: 1.5,
        }}>
          All emails are automatically BCC'd to: <strong style={{ color: COLORS.textPrimary }}>{settings.bcc_email || '80yearsofconnection@gmail.com'}</strong>
        </div>
      </div>

      {/* Email Templates Section */}
      <div style={{
        background: COLORS.cardBg,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 12,
        padding: 20,
        marginBottom: 20,
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <Mail size={16} color={COLORS.gold} />
          <span style={{ fontSize: 14, fontWeight: 600, color: COLORS.textPrimary }}>
            Auto-Reply Email Templates ({emailTmpls.length})
          </span>
        </div>

        {emailTmpls.length === 0 && (
          <div style={{ fontSize: 13, color: COLORS.textMuted, fontStyle: 'italic', padding: '12px 0' }}>
            No email templates configured.
          </div>
        )}

        {emailTmpls.map(t => (
          <div key={t.id} style={{
            border: `1px solid ${COLORS.border}`,
            borderRadius: 10,
            padding: '14px 16px',
            marginBottom: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: COLORS.surfaceWarm,
            transition: 'all 150ms ease',
          }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 500, color: COLORS.textPrimary }}>{t.name}</div>
              <div style={{ fontSize: 12, color: COLORS.textSecondary, marginTop: 3 }}>Subject: {t.subject}</div>
              <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                {t.is_active && (
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: COLORS.successLight, color: COLORS.success }}>ACTIVE</span>
                )}
                {t.is_live && (
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: COLORS.warningLight, color: COLORS.warning }}>LIVE SOURCE</span>
                )}
                {t.is_builtin && !t.is_live && (
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: COLORS.infoLight, color: COLORS.info }}>BUILT-IN</span>
                )}
                <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: 'rgba(249,115,22,0.15)', color: '#f97316' }}>AUTO-REPLY</span>
                {t.template_type && (
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: COLORS.goldLight, color: COLORS.gold }}>
                    {(t.template_type || '').replace(/_/g, ' ').toUpperCase()}
                  </span>
                )}
                {/* Pass 5 / M14 — Milo eligibility badge. Visible on
                    every row whose governance state is known, regardless
                    of whether the controls below are rendered. */}
                {sysRules && t.is_milo_eligible && (
                  <span data-testid={`milo-eligible-badge-${t.id}`}
                    style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: 'rgba(34,197,94,0.15)', color: '#16a34a' }}>
                    MILO ELIGIBLE
                  </span>
                )}
              </div>
              {/* Pass 5 / M14 — governance controls. Hidden when
                  sysRules hasn't loaded (no /api/system-rules endpoint
                  available, e.g. backend not yet upgraded) and on
                  built-in/live rows where governance does not apply.
                  Built-in templates remain reachable via the API for
                  admins who want to opt them in deliberately, but the
                  default surface is operator-curated rows only. */}
              {sysRules && !t.is_live && (
                <div data-testid={`milo-governance-${t.id}`}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10, paddingTop: 10,
                    borderTop: `1px dashed ${COLORS.border}`, opacity: savingId === t.id ? 0.5 : 1,
                    flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: COLORS.textSecondary, cursor: 'pointer', fontWeight: 600 }}>
                    <input type="checkbox"
                      data-testid={`milo-eligible-toggle-${t.id}`}
                      checked={!!t.is_milo_eligible}
                      disabled={savingId === t.id}
                      onChange={(e) => handleUpdateGovernance(t.id, { is_milo_eligible: e.target.checked })}
                      style={{ cursor: 'pointer', accentColor: COLORS.gold }} />
                    Milo eligible
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: COLORS.textSecondary }}>
                    <span style={{ fontWeight: 600 }}>Audience:</span>
                    <select
                      data-testid={`milo-audience-select-${t.id}`}
                      value={t.audience_scope || 'all_guests'}
                      disabled={savingId === t.id || !t.is_milo_eligible}
                      onChange={(e) => handleUpdateGovernance(t.id, { audience_scope: e.target.value })}
                      style={{ padding: '4px 8px', borderRadius: 4, border: `1px solid ${COLORS.border}`,
                        background: COLORS.inputBg, fontSize: 11, color: COLORS.textPrimary,
                        cursor: (savingId === t.id || !t.is_milo_eligible) ? 'not-allowed' : 'pointer' }}>
                      {(sysRules.audience_scopes || []).map(s => (
                        <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
                      ))}
                    </select>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: COLORS.textSecondary }}>
                    <span style={{ fontWeight: 600 }}>Timing:</span>
                    <select
                      data-testid={`milo-timing-select-${t.id}`}
                      value={t.timing_window || 'any'}
                      disabled={savingId === t.id || !t.is_milo_eligible}
                      onChange={(e) => handleUpdateGovernance(t.id, { timing_window: e.target.value })}
                      style={{ padding: '4px 8px', borderRadius: 4, border: `1px solid ${COLORS.border}`,
                        background: COLORS.inputBg, fontSize: 11, color: COLORS.textPrimary,
                        cursor: (savingId === t.id || !t.is_milo_eligible) ? 'not-allowed' : 'pointer' }}>
                      {(sysRules.timing_windows || []).map(w => (
                        <option key={w} value={w}>{w.replace(/_/g, ' ')}</option>
                      ))}
                    </select>
                  </label>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, marginLeft: 16 }}>
              <button
                onClick={() => setPreview(t)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '6px 12px',
                  borderRadius: 6,
                  border: 'none',
                  background: COLORS.gold,
                  color: '#fff',
                  fontSize: 12,
                  cursor: 'pointer',
                  fontWeight: 500,
                }}
                data-testid={`preview-email-${t.id}`}
              >
                <Eye size={13}/>Preview
              </button>
              {!t.is_active && !t.is_live && (
                <button
                  onClick={() => handleActivate(t.id)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: 6,
                    border: `1px solid ${COLORS.gold}`,
                    background: 'transparent',
                    color: COLORS.gold,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                  data-testid={`activate-${t.id}`}
                >
                  Set Active
                </button>
              )}
              {!t.is_builtin && !t.is_live && (
                <button
                  onClick={() => handleDeleteTmpl(t.id)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: COLORS.textMuted, padding: 4 }}
                  data-testid={`delete-email-${t.id}`}
                >
                  <Trash2 size={14}/>
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* SMS Templates Section */}
      <div style={{
        background: COLORS.cardBg,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 12,
        padding: 20,
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <MessageSquare size={16} color={COLORS.gold} />
            <span style={{ fontSize: 14, fontWeight: 600, color: COLORS.textPrimary }}>
              SMS/MMS Templates ({smsTmpls.length})
            </span>
          </div>
          <button
            onClick={() => setShowCreateSms(!showCreateSms)}
            style={{
              padding: '6px 14px',
              borderRadius: 6,
              border: 'none',
              background: COLORS.gold,
              color: '#fff',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
            }}
            data-testid="create-sms-btn"
          >
            {showCreateSms ? 'Cancel' : '+ Create New'}
          </button>
        </div>

        {showCreateSms && (
          <div style={{
            border: `1px solid ${COLORS.borderMedium}`,
            borderRadius: 10,
            padding: 16,
            marginBottom: 16,
            background: COLORS.surfaceWarm,
          }}>
            <input
              placeholder="Template Name"
              value={newSms.name}
              onChange={e => setNewSms(s => ({ ...s, name: e.target.value }))}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: 8,
                border: `1px solid ${COLORS.border}`,
                fontSize: 13,
                marginBottom: 10,
                outline: 'none',
                boxSizing: 'border-box',
                background: COLORS.inputBg,
                color: COLORS.textPrimary,
              }}
              data-testid="sms-name-input"
            />
            <select
              value={newSms.template_type}
              onChange={e => setNewSms(s => ({ ...s, template_type: e.target.value }))}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: 8,
                border: `1px solid ${COLORS.border}`,
                fontSize: 13,
                marginBottom: 10,
                outline: 'none',
                boxSizing: 'border-box',
                background: COLORS.cardBg,
                color: COLORS.textPrimary,
              }}
              data-testid="sms-type-select"
            >
              {['save_the_date', 'invitation', 'thank_you', 'reminder', 'custom'].map(t => (
                <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
              ))}
            </select>
            <textarea
              placeholder="Message text"
              value={newSms.message_text}
              onChange={e => setNewSms(s => ({ ...s, message_text: e.target.value }))}
              maxLength={160}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: 8,
                border: `1px solid ${COLORS.border}`,
                fontSize: 13,
                minHeight: 80,
                resize: 'vertical',
                outline: 'none',
                boxSizing: 'border-box',
                background: COLORS.inputBg,
                color: COLORS.textPrimary,
              }}
              data-testid="sms-message-input"
            />
            <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 4, textAlign: 'right' }}>
              {newSms.message_text.length}/160
            </div>
            <button
              onClick={handleCreateSms}
              style={{
                marginTop: 10,
                padding: '10px 20px',
                borderRadius: 8,
                border: 'none',
                background: COLORS.gold,
                color: '#fff',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
              data-testid="save-sms-btn"
            >
              Create Template
            </button>
          </div>
        )}

        {smsTmpls.length === 0 && !showCreateSms && (
          <div style={{ fontSize: 13, color: COLORS.textMuted, fontStyle: 'italic', padding: '12px 0' }}>
            No SMS templates yet. Create one to get started.
          </div>
        )}

        {smsTmpls.map(t => (
          <div key={t.id} style={{
            border: `1px solid ${COLORS.border}`,
            borderRadius: 10,
            padding: '14px 16px',
            marginBottom: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: COLORS.surfaceWarm,
          }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 500, color: COLORS.textPrimary }}>{t.name}</div>
              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                <span style={{
                  fontSize: 10,
                  fontWeight: 700,
                  padding: '2px 8px',
                  borderRadius: 4,
                  background: t.media_urls?.length ? 'rgba(139,92,246,0.15)' : COLORS.infoLight,
                  color: t.media_urls?.length ? '#8b5cf6' : COLORS.info,
                }}>
                  {t.media_urls?.length ? 'MMS' : 'SMS'}
                </span>
                {t.template_type && (
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: COLORS.goldLight, color: COLORS.gold }}>
                    {(t.template_type || '').replace(/_/g, ' ').toUpperCase()}
                  </span>
                )}
                {t.media_urls?.length > 0 && (
                  <span style={{ fontSize: 10, color: COLORS.textMuted }}>{t.media_urls.length} media attached</span>
                )}
              </div>
              <div style={{ fontSize: 12, color: COLORS.textSecondary, marginTop: 4 }}>
                {t.message_text.substring(0, 80)}{t.message_text.length > 80 ? '...' : ''}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginLeft: 16 }}>
              <button
                onClick={() => setPreview(t)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '6px 12px',
                  borderRadius: 6,
                  border: 'none',
                  background: COLORS.gold,
                  color: '#fff',
                  fontSize: 12,
                  cursor: 'pointer',
                  fontWeight: 500,
                }}
                data-testid={`preview-sms-${t.id}`}
              >
                <Eye size={13}/>Preview
              </button>
              <button
                onClick={() => handleDeleteSms(t.id)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: COLORS.textMuted, padding: 4 }}
                data-testid={`delete-sms-${t.id}`}
              >
                <Trash2 size={14}/>
              </button>
            </div>
          </div>
        ))}

        {/* SMS Provider Notice */}
        <div style={{
          marginTop: 16,
          background: COLORS.goldLight,
          border: `1px solid ${COLORS.border}`,
          borderRadius: 8,
          padding: 14,
          fontSize: 13,
          color: COLORS.textSecondary,
          lineHeight: 1.5,
        }}>
          <MessageSquare size={14} style={{ marginRight: 6, verticalAlign: 'middle', color: COLORS.gold }} />
          SMS sending via Telnyx is stubbed for this version. Configure your Telnyx API key and 10DLC-approved number when ready.
        </div>
      </div>

      {/* Preview Modal */}
      {preview && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setPreview(null)}
          data-testid="template-preview-modal"
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: COLORS.cardBg,
              borderRadius: 16,
              padding: 28,
              maxWidth: 560,
              width: '90%',
              maxHeight: '80vh',
              overflowY: 'auto',
              border: `1px solid ${COLORS.border}`,
              boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
            }}
          >
            <h3 style={{ fontSize: 18, fontWeight: 600, color: COLORS.textPrimary, marginBottom: 4 }}>{preview.name}</h3>
            <div style={{ fontSize: 12, color: COLORS.textSecondary, marginBottom: 16 }}>
              {preview.subject && <span>Subject: {preview.subject} · </span>}
              Type: {preview.template_type}
            </div>
            {preview.html_content ? (
              <div style={{ border: `1px solid ${COLORS.border}`, borderRadius: 8, overflow: 'hidden' }}>
                <iframe title="preview" srcDoc={preview.html_content} style={{ width: '100%', height: 400, border: 'none' }} />
              </div>
            ) : preview.message_text ? (
              <div style={{
                background: COLORS.surfaceWarm,
                borderRadius: 8,
                padding: 16,
                fontSize: 14,
                color: COLORS.textPrimary,
                lineHeight: 1.6,
              }}>
                {preview.message_text}
              </div>
            ) : null}
            <button
              onClick={() => setPreview(null)}
              style={{
                marginTop: 20,
                width: '100%',
                padding: 12,
                borderRadius: 8,
                background: COLORS.gold,
                color: '#fff',
                border: 'none',
                fontSize: 14,
                cursor: 'pointer',
                fontWeight: 600,
              }}
              data-testid="close-preview-btn"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageTemplatesTab;
